<template>
  <div>
    <v-dialog
      v-model="dialogVisible"
      persistent
      :fullscreen="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm"
      :max-width="
        $vuetify.breakpoint.xs || $vuetify.breakpoint.sm ? null : '900px'
      "
      scrollable
    >
      <v-card>
        <v-card-title>
          <span class="dialog-title">{{ dialogTitle }}</span>
        </v-card-title>

        <div class="pa-0" style="position: relative; flex: 1; overflow-y: auto">
          <!-- Contract existence check -->
          <div v-if="!contract" class="pa-4 text-center">
            <p>{{ $t("digitalContracts.contractNotFound") }}</p>
          </div>

          <div
            v-else-if="contract.contract_file?.has_file"
            class="pdf-container pdf-container-height"
            style="
              border-top: 1px solid #e0e0e0;
              border-bottom: 1px solid #e0e0e0;
              overflow-y: auto;
            "
          >
            <div v-if="totalPages === 0" class="pdf-loading">
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
              <p class="mt-2">{{ $t("digitalContracts.loadingDocument") }}</p>
            </div>

            <div class="pdf-pages-all">
              <canvas
                v-for="pageNum in totalPages"
                :key="pageNum"
                :ref="`pdfCanvas${pageNum}`"
                :data-page="pageNum"
                class="pdf-page"
                style="margin-bottom: 20px"
              ></canvas>
            </div>
          </div>

          <div
            v-if="showSignatureWidget"
            class="signature-overlay"
            @click="onOverlayClick"
          >
            <div class="signature-modal" @click.stop>
              <div class="signature-header">
                <h3 class="signature-title">
                  {{ $t("digitalContracts.electronicSignature") }}
                </h3>
                <v-btn
                  icon
                  small
                  class="signature-close"
                  @click="cancelSignature"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
              <p class="signature-description">
                {{ $t("digitalContracts.signatureInstructions") }}
              </p>
              <div class="signature-container">
                <canvas
                  ref="signatureCanvas"
                  width="600"
                  height="200"
                  class="signature-canvas"
                  @mousedown="startSigning"
                  @mousemove="continueSigning"
                  @mouseup="stopSigning"
                  @touchstart="startSigning"
                  @touchmove="continueSigning"
                  @touchend="stopSigning"
                ></canvas>
                <div class="signature-actions">
                  <v-btn
                    outlined
                    color="grey"
                    class="mr-3"
                    :disabled="isSubmittingSignature"
                    @click="clearSignature"
                  >
                    <v-icon left>mdi-delete</v-icon>
                    {{ $t("digitalContracts.clearSignature") }}
                  </v-btn>
                  <v-btn
                    color="primary"
                    :disabled="!hasSignature || isSubmittingSignature"
                    :loading="isSubmittingSignature"
                    @click="confirmSignature"
                  >
                    <v-icon left>mdi-check</v-icon>
                    {{ $t("digitalContracts.confirmSignature") }}
                  </v-btn>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="
              contract && contract.user_action === 'accept' && !hasScrolledToEnd
            "
            class="pa-4 pb-2"
          >
            <div class="text-caption text--secondary">
              {{ $t("digitalContracts.scrollToContinue") }}
            </div>
          </div>
        </div>

        <v-card-actions class="mt-3">
          <v-btn
            v-if="contract?.contract_file?.url"
            icon
            color="primary"
            :title="$t('digitalContracts.download')"
            @click="downloadContract"
          >
            <IconCustom name="icn20_download_2" />
          </v-btn>

          <v-spacer></v-spacer>

          <template v-if="contract && contract.user_action === 'sign'">
            <v-btn
              v-if="!showSignatureWidget"
              color="primary"
              @click="showSignatureWidget = true"
            >
              {{ $t("digitalContracts.sign") }}
            </v-btn>
          </template>

          <template v-else-if="contract && contract.user_action === 'accept'">
            <v-btn
              color="grey"
              text
              :disabled="!hasScrolledToEnd || isSubmittingAction"
              :loading="isSubmittingAction && lastAction === 'declined'"
              @click="showDeclineConfirmDialog"
            >
              {{ $t("digitalContracts.decline") }}
            </v-btn>
            <v-btn
              color="primary"
              :disabled="!hasScrolledToEnd || isSubmittingAction"
              :loading="isSubmittingAction && lastAction === 'accepted'"
              @click="handleAcceptedAction"
            >
              {{ $t("digitalContracts.accept") }}
            </v-btn>
          </template>

          <template v-else-if="contract && contract.user_action === 'read'">
            <v-btn
              color="primary"
              :disabled="isSubmittingAction"
              :loading="isSubmittingAction && lastAction === 'read'"
              @click="handleReadAction"
            >
              {{ $t("digitalContracts.readConfirm") }}
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog
      v-if="showDeclineConfirm"
      :show-dialog="showDeclineConfirm"
      :text="$t('digitalContracts.declineConfirmText')"
      @closeConfirmDialog="onDeclineConfirmClose"
    />
  </div>
</template>

<script>
import * as pdfjsLib from "pdfjs-dist/webpack";
import ConfirmDialog from "@/components/helper/ConfirmDialog.vue";
import IconCustom from "@/components/helper/IconCustom.vue";

export default {
  name: "DigitalContractsDialog",
  components: {
    ConfirmDialog,
    IconCustom,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    contract: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      dialogVisible: false,
      showSignatureWidget: false,

      isSigningActive: false,
      hasSignature: false,
      signatureContext: null,
      lastSignaturePoint: null,
      totalPages: 0,
      pdfDocument: null,
      renderedPages: {},
      isSubmittingSignature: false,
      isSubmittingAction: false,
      originalViewports: {}, // Store original viewports for each page
      hasScrolledToEnd: false, // Track if user scrolled to the end
      scrollHandler: null, // Scroll handler for accept action
      lastAction: null, // Last executed action for loading state
      showDeclineConfirm: false, // Show decline confirmation dialog
    };
  },
  computed: {
    dialogTitle() {
      if (!this.contract) return this.$t("digitalContracts.contract");

      if (this.contract.user_action === "sign") {
        if (this.contract.amendment) {
          return this.$t("digitalContracts.amendmentTitle");
        } else if (this.contract.type === "user") {
          return this.$t("digitalContracts.userContractTitle");
        } else if (this.contract.type === "partner") {
          return this.$t("digitalContracts.partnerContractTitle");
        }
        return this.$t("digitalContracts.contractExecution");
      }
      return this.contract.name || this.$t("digitalContracts.contract");
    },
  },
  watch: {
    visible: {
      handler(newVal) {
        this.dialogVisible = newVal;
        if (newVal) {
          this.resetDialog();
          this.$nextTick(() => {
            this.loadPdf();
            // Add scroll handler for accept action
            if (this.contract?.user_action === "accept") {
              this.setupScrollTracker();
            }
          });
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.removeScrollTracker();
  },
  methods: {
    // Download contract file in new tab
    downloadContract() {
      if (this.contract?.contract_file?.url) {
        window.open(this.contract.contract_file.url, "_blank");
      }
    },

    resetDialog() {
      this.showSignatureWidget = false;

      this.hasSignature = false;
      this.isSigningActive = false;
      this.lastSignaturePoint = null;
      this.totalPages = 0;
      this.pdfDocument = null;
      this.renderedPages = {};
      this.isSubmittingSignature = false;
      this.isSubmittingAction = false;
      this.originalViewports = {};
      this.hasScrolledToEnd = false;
      this.lastAction = null;
      this.showDeclineConfirm = false;
      this.removeScrollTracker();
    },

    closeDialog() {
      this.$emit("close");
    },

    async loadPdf() {
      if (
        !this.contract.contract_file?.has_file ||
        !this.contract.contract_file?.url
      ) {
        return;
      }

      try {
        this.pdfDocument = await pdfjsLib.getDocument(
          this.contract.contract_file.url
        ).promise;
        this.totalPages = this.pdfDocument.numPages;

        // Render all pages at once
        for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
          await this.renderPage(pageNum);
        }
      } catch (error) {
        this.$emit("error", this.$t("digitalContracts.errorLoadingDocument"));
      }
    },

    async renderPage(pageNum) {
      if (!this.pdfDocument || this.renderedPages[pageNum]) {
        return;
      }

      try {
        const page = await this.pdfDocument.getPage(pageNum);
        const canvas = this.$refs[`pdfCanvas${pageNum}`]?.[0];

        if (!canvas) {
          // Wait for next tick to render canvas
          await this.$nextTick();
          const canvasAfterTick = this.$refs[`pdfCanvas${pageNum}`]?.[0];
          if (!canvasAfterTick) return;
          return this.renderPageOnCanvas(page, canvasAfterTick, pageNum);
        }

        return this.renderPageOnCanvas(page, canvas, pageNum);
      } catch (error) {
        console.warn("Error rendering page:", error);
      }
    },

    async renderPageOnCanvas(page, canvas, pageNum) {
      const context = canvas.getContext("2d");

      // Save original viewport (scale 1.0) for coordinate recalculation
      this.originalViewports[pageNum] = page.getViewport({ scale: 1.0 });

      // Calculate optimal scale for container
      const containerWidth = canvas.parentElement.clientWidth - 40; // account for padding
      const viewport = page.getViewport({ scale: 1 });
      const maxScale = this.$vuetify.breakpoint.mobile ? 4.0 : 8.0; // Different scale for mobile vs desktop
      const scale = Math.min(containerWidth / viewport.width, maxScale);
      const scaledViewport = page.getViewport({ scale });

      // For mobile, use desktop resolution but display at mobile size
      const isMobile = this.$vuetify.breakpoint.mobile;
      const renderViewport = page.getViewport({ scale });

      // Set canvas size to high resolution
      canvas.height = renderViewport.height;
      canvas.width = renderViewport.width;

      // Set CSS size for display (smaller for mobile)
      if (isMobile) {
        canvas.style.width = scaledViewport.width + "px";
        canvas.style.height = scaledViewport.height + "px";
      }

      const renderContext = {
        canvasContext: context,
        viewport: renderViewport,
      };

      await page.render(renderContext).promise;

      // Fill PDF fields with KYC data
      this.fillPdfFields(canvas, pageNum);

      this.renderedPages[pageNum] = true;
    },

    // Scroll Tracking Methods
    setupScrollTracker() {
      this.$nextTick(() => {
        const container = document.querySelector(".pdf-container");
        if (container) {
          this.scrollHandler = this.throttle(this.checkScrollToEnd, 100);
          container.addEventListener("scroll", this.scrollHandler);
        }
      });
    },

    removeScrollTracker() {
      const container = document.querySelector(".pdf-container");
      if (container && this.scrollHandler) {
        container.removeEventListener("scroll", this.scrollHandler);
        this.scrollHandler = null;
      }
    },

    checkScrollToEnd() {
      const container = document.querySelector(".pdf-container");
      if (!container) return;

      // Check if user scrolled to the end
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // Add small buffer (10px) for correct operation
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

      if (isAtBottom && !this.hasScrolledToEnd) {
        this.hasScrolledToEnd = true;
      }
    },

    throttle(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    // PDF Fields Methods
    fillPdfFields(canvas, pageNumber) {
      if (
        !this.contract.contract_fields ||
        !this.contract.kyc_data ||
        !this.originalViewports[pageNumber]
      )
        return;

      const fields = Array.isArray(this.contract.contract_fields)
        ? this.contract.contract_fields
        : JSON.parse(this.contract.contract_fields);

      const ctx = canvas.getContext("2d");
      const originalViewport = this.originalViewports[pageNumber];

      // Calculate scale between canvas and original PDF
      const scaleX = canvas.width / originalViewport.width;
      const scaleY = canvas.height / originalViewport.height;
      const scale = Math.min(scaleX, scaleY);

      // PDF font size is usually 10-12pt, scale it for canvas
      const pdfFontSize = 12; // standard PDF font size (in points)
      const canvasFontSize = pdfFontSize * scale;

      // Set font like in standard PDF
      ctx.font = `${canvasFontSize}px "Times New Roman", Times, serif`;
      ctx.fillStyle = "#000000";
      ctx.textBaseline = "baseline";

      fields.forEach((field) => {
        if (field.page === pageNumber && field.key !== "signature") {
          const value = this.getKycValue(field.key);
          if (value) {
            // Recalculate coordinates PDF → Canvas
            const canvasX = (field.x / originalViewport.width) * canvas.width;
            const canvasY = (field.y / originalViewport.height) * canvas.height;

            ctx.fillText(value, canvasX, canvasY);
          }
        }
      });
    },

    getKycValue(key) {
      if (!this.contract.kyc_data) return "";

      // Key mapping
      const keyMap = {
        name: this.contract.kyc_data.name,
        first_name: this.contract.kyc_data.first_name,
        last_name: this.contract.kyc_data.last_name,
        date_of_birth: this.contract.kyc_data.date_of_birth,
        document_type: this.contract.kyc_data.document_type,
        document_number: this.contract.kyc_data.document_number,
        date: new Date().toLocaleDateString("ru-RU"), // Current date
      };

      const value = keyMap[key] || "";
      // Convert to uppercase for PDF fields
      return typeof value === "string" ? value.toUpperCase() : value;
    },

    // Helper method to transform PDF coordinates to canvas coordinates
    _transformCoordinates(x, y, originalViewport, canvas) {
      return {
        x: (x / originalViewport.width) * canvas.width,
        y: (y / originalViewport.height) * canvas.height,
      };
    },

    // Helper method to create base PDF document
    _createBasePdf() {
      return new Promise((resolve) => {
        import("jspdf").then(({ jsPDF }) => {
          const pdf = new jsPDF({
            orientation: "portrait",
            unit: "pt",
            format: "a4",
            compress: true, // Enable compression
            precision: 2, // Reduce precision to save space
          });
          resolve(pdf);
        });
      });
    },

    // Helper method to add page to PDF with proper scaling
    async _addPageToPdf(pdf, canvas, pageNum, isFirstPage = false) {
      if (!isFirstPage) {
        pdf.addPage();
      }

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Use high quality for better text readability
      const canvasImageData = canvas.toDataURL("image/jpeg", 0.95);

      // Scale image to PDF page size
      const ratio = Math.min(
        pdfWidth / canvas.width,
        pdfHeight / canvas.height
      );
      const scaledWidth = canvas.width * ratio;
      const scaledHeight = canvas.height * ratio;

      // Center image
      const x = (pdfWidth - scaledWidth) / 2;
      const y = (pdfHeight - scaledHeight) / 2;

      pdf.addImage(
        canvasImageData,
        "PNG",
        x,
        y,
        scaledWidth,
        scaledHeight,
        undefined,
        "FAST"
      );
    },

    // Helper method to get signature fields
    _getSignatureFields() {
      if (!this.contract.contract_fields) return [];

      const fields = Array.isArray(this.contract.contract_fields)
        ? this.contract.contract_fields
        : JSON.parse(this.contract.contract_fields);

      return fields.filter((field) => field.key === "signature");
    },

    // Helper method to add signature to canvas
    async _addSignatureToCanvas(
      canvas,
      signatureData,
      signatureField,
      originalViewport
    ) {
      const ctx = canvas.getContext("2d");
      const { x: canvasX, y: canvasY } = this._transformCoordinates(
        signatureField.x,
        signatureField.y,
        originalViewport,
        canvas
      );

      // Create signature image
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = () => {
          try {
            // Add signature to canvas with original size (not scaled)
            const signatureWidth = 480; // Original signature width
            const signatureHeight = 120; // Original signature height

            // Position so JSON coordinates are in the center of signature
            const centerX = canvasX - signatureWidth / 2;
            const centerY = canvasY - signatureHeight / 2;

            ctx.drawImage(
              img,
              centerX,
              centerY,
              signatureWidth,
              signatureHeight
            );
            resolve();
          } catch (error) {
            reject(error);
          }
        };
        img.onerror = () => reject(new Error("Signature loading error"));
        img.src = signatureData;
      });
    },

    async createSignedPdfFile(signatureData) {
      // Create base PDF document
      const pdf = await this._createBasePdf();
      const signatureFields = this._getSignatureFields();

      // Process all pages
      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        const pageCanvas = this.$refs[`pdfCanvas${pageNum}`]?.[0];
        if (!pageCanvas) {
          continue;
        }

        // Clone page canvas
        const finalCanvas = document.createElement("canvas");
        const ctx = finalCanvas.getContext("2d");
        finalCanvas.width = pageCanvas.width;
        finalCanvas.height = pageCanvas.height;

        // Copy main content
        ctx.drawImage(pageCanvas, 0, 0);

        // Add signature if there's a signature field on this page
        const signatureField = signatureFields.find(
          (field) => field.page === pageNum
        );

        if (signatureField && this.originalViewports[pageNum]) {
          await this._addSignatureToCanvas(
            finalCanvas,
            signatureData,
            signatureField,
            this.originalViewports[pageNum]
          );
        }

        // Add page to PDF
        await this._addPageToPdf(pdf, finalCanvas, pageNum, pageNum === 1);
      }

      // Get PDF as blob
      const pdfBlob = pdf.output("blob");

      // Validate file size (50MB limit)
      if (pdfBlob.size > 50 * 1024 * 1024) {
        throw new Error("Generated PDF file is too large");
      }

      // Create File object
      const file = new File([pdfBlob], "signed_contract.pdf", {
        type: "application/pdf",
        lastModified: Date.now(),
      });

      return file;
    },

    // Helper method to clear sensitive data from memory
    _clearCanvasData() {
      try {
        // Clear all canvas elements
        for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
          const canvas = this.$refs[`pdfCanvas${pageNum}`]?.[0];
          if (canvas) {
            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, canvas.width, canvas.height);
          }
        }

        // Clear signature canvas
        const signatureCanvas = this.$refs.signatureCanvas;
        if (signatureCanvas) {
          const ctx = signatureCanvas.getContext("2d");
          ctx.clearRect(0, 0, signatureCanvas.width, signatureCanvas.height);
        }
      } catch (error) {
        console.warn("Error clearing canvas data:", error);
      }
    },

    async createPdfFile() {
      // Create base PDF document
      const pdf = await this._createBasePdf();

      // Process all pages
      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        const pageCanvas = this.$refs[`pdfCanvas${pageNum}`]?.[0];
        if (!pageCanvas) {
          continue;
        }

        // Add page to PDF
        await this._addPageToPdf(pdf, pageCanvas, pageNum, pageNum === 1);
      }

      // Get PDF as blob
      const pdfBlob = pdf.output("blob");

      // Validate file size (50MB limit)
      if (pdfBlob.size > 50 * 1024 * 1024) {
        throw new Error(this.$t("digitalContracts.errorPdfTooLarge"));
      }

      // Create File object
      const file = new File([pdfBlob], "contract.pdf", {
        type: "application/pdf",
        lastModified: Date.now(),
      });

      // Clear sensitive data from memory
      this._clearCanvasData();

      return file;
    },

    async handleActionWithFile(action) {
      this.isSubmittingAction = true;
      this.lastAction = action;

      try {
        // Create PDF file with filled KYC data
        const pdfFile = await this.createPdfFile();

        await this.handleUserAction(action, null, pdfFile);
      } catch (error) {
        this.$emit("error", this.$t("digitalContracts.errorExecutingAction"));
        this.isSubmittingAction = false;
        this.lastAction = null;
      }
    },

    showDeclineConfirmDialog() {
      this.showDeclineConfirm = true;
    },

    async onDeclineConfirmClose(confirmed) {
      this.showDeclineConfirm = false;
      if (confirmed) {
        // Also send file when declining document
        await this.handleActionWithFile("declined");
      }
    },

    async handleAcceptedAction() {
      // Send file with filled data when accepting document
      await this.handleActionWithFile("accepted");
    },

    async handleReadAction() {
      // Also send file with filled data when reading document
      await this.handleActionWithFile("read");
    },

    // Signature Methods
    startSigning(event) {
      this.isSigningActive = true;
      this.hasSignature = true;

      if (!this.signatureContext) {
        const canvas = this.$refs.signatureCanvas;
        this.signatureContext = canvas.getContext("2d");
        this.signatureContext.strokeStyle = "#1976d2";
        this.signatureContext.lineWidth = 2;
        this.signatureContext.lineCap = "round";
      }

      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x =
        ((event.clientX || event.touches[0].clientX) - rect.left) * scaleX;
      const y =
        ((event.clientY || event.touches[0].clientY) - rect.top) * scaleY;

      this.lastSignaturePoint = { x, y };

      event.preventDefault();
    },

    continueSigning(event) {
      if (!this.isSigningActive || !this.lastSignaturePoint) return;

      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x =
        ((event.clientX || event.touches[0].clientX) - rect.left) * scaleX;
      const y =
        ((event.clientY || event.touches[0].clientY) - rect.top) * scaleY;

      this.signatureContext.beginPath();
      this.signatureContext.moveTo(
        this.lastSignaturePoint.x,
        this.lastSignaturePoint.y
      );
      this.signatureContext.lineTo(x, y);
      this.signatureContext.stroke();

      this.lastSignaturePoint = { x, y };

      event.preventDefault();
    },

    stopSigning() {
      this.isSigningActive = false;
      this.lastSignaturePoint = null;
    },

    clearSignature() {
      const canvas = this.$refs.signatureCanvas;
      if (canvas && this.signatureContext) {
        this.signatureContext.clearRect(0, 0, canvas.width, canvas.height);
        this.hasSignature = false;
      }
    },

    async confirmSignature() {
      this.isSubmittingSignature = true;
      try {
        const canvas = this.$refs.signatureCanvas;
        const signatureData = canvas.toDataURL();

        // Create file with signature and KYC data
        const pdfFile = await this.createSignedPdfFile(signatureData);

        await this.handleUserAction("signed", signatureData, pdfFile);
      } finally {
        this.isSubmittingSignature = false;
      }
    },

    cancelSignature() {
      this.showSignatureWidget = false;
      this.clearSignature();
      this.signatureContext = null;
      this.isSigningActive = false;
      this.lastSignaturePoint = null;
    },

    onOverlayClick() {
      // Close when clicking on overlay background
      this.cancelSignature();
    },

    // Helper method to validate UUID format
    _isValidUUID(uuid) {
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return uuidRegex.test(uuid);
    },

    // Helper method to validate action parameter
    _isValidAction(action) {
      const validActions = ["signed", "accepted", "declined", "read"];
      return validActions.includes(action);
    },

    async handleUserAction(action, signature = null, file = null) {
      if (!this.contract) {
        return;
      }

      // Prevent double submission (but allow signature action when called from confirmSignature)
      if (
        this.isSubmittingAction ||
        (this.isSubmittingSignature && action !== "signed")
      ) {
        return;
      }

      // Validate action parameter
      if (!this._isValidAction(action)) {
        this.$emit("error", this.$t("digitalContracts.errorInvalidAction"));
        return;
      }

      // Validate contract UUID
      if (!this.contract.uuid || !this._isValidUUID(this.contract.uuid)) {
        this.$emit("error", this.$t("digitalContracts.errorInvalidContract"));
        return;
      }

      // For signature, loading state is already managed in confirmSignature
      if (action !== "signed") {
        this.isSubmittingAction = true;
        this.lastAction = action;
      }

      try {
        const mutation = require("@/graphql/mutations/DigitalContractUserAction.gql");

        if (!file) {
          throw new Error("File is required for submission");
        }

        // Validate file size and type
        if (file.size > 50 * 1024 * 1024) {
          // 50MB limit
          throw new Error(this.$t("digitalContracts.errorFileTooLarge"));
        }

        if (file.type !== "application/pdf") {
          throw new Error(this.$t("digitalContracts.errorInvalidFileType"));
        }

        const variables = {
          contract_uuid: this.contract.uuid,
          action: action,
          file: file,
        };

        if (signature) {
          variables.signature = signature;
        }

        const result = await this.$apollo.mutate({
          mutation,
          variables,
          context: {
            hasUpload: true,
          },
        });

        if (result.data?.digitalContractUserAction?.success) {
          // Mark contract as processed to prevent re-opening
          this.$set(this.contract, "isProcessed", true);

          // Clear sensitive data from memory after successful submission
          this._clearCanvasData();

          // Show success notification via helper
          if (action === "signed") {
            this.responseHandler(this.$t("digitalContracts.successSigned"));
          }

          this.$emit("success", action);
          this.closeDialog();

          // Redirect to dashboard
          this.$router.push("/dashboard");
        } else {
          const message =
            result.data?.digitalContractUserAction?.message ||
            this.$t("digitalContracts.errorOccurred");
          this.$emit("error", message);
        }
      } catch (error) {
        this.$emit(
          "error",
          error.message || this.$t("digitalContracts.errorOccurred")
        );
      } finally {
        if (action !== "signed") {
          this.isSubmittingAction = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.dialog-title {
  font-family: "Montserrat", sans-serif;
  font-weight: 600;
  font-size: 1.25rem;
  line-height: 2rem;
  letter-spacing: 0.0125em;
}

.pdf-container {
  background: #f5f5f5;
  padding: 10px;
}

.pdf-controls {
  background: white;
  padding: 8px;
  border-radius: 4px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.pdf-pages {
  display: flex;
  justify-content: center;
}

.pdf-pages-all {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pdf-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--v-text-base);
}

.pdf-page {
  max-width: 100%;
  height: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  background: white;
}

/* Signature Overlay Styles */
.signature-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
}

.signature-modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 700px;
  width: 90%;
  max-height: 90%;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease-out;
}

.signature-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.signature-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--v-text-base);
}

.signature-close {
  margin-left: auto;
}

.signature-description {
  color: var(--v-text-base);
  margin-bottom: 20px;
  text-align: center;
  font-size: 0.875rem;
}

.signature-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.signature-canvas {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  width: 100%;
  max-width: 600px;
  cursor: crosshair;
  margin-bottom: 16px;
}

.signature-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
}

/* Dark theme support */
.theme--dark .signature-modal {
  background: var(--v-surface-base);
  color: var(--v-text-base);
}

.theme--dark .signature-canvas {
  border-color: var(--v-border-base);
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@media (max-width: 768px) {
  .dialog-title {
    font-size: 0.625rem;
  }

  .pdf-container-height {
    height: 100% !important;
  }

  .signature-canvas {
    width: 100% !important;
    max-width: 100% !important;
  }

  .pdf-container {
    padding: 0 !important;
    margin: 0 !important;
    margin-left: 0 !important;
    padding-left: 0 !important;
  }
}

@media (max-width: 600px) {
  .signature-actions {
    flex-direction: column !important;
    gap: 8px !important;
  }

  .signature-actions .v-btn {
    width: 100% !important;
    margin: 0 !important;
  }

  .dialog-title {
    font-size: 0.8rem !important;
  }

  .signature-title {
    font-size: 0.8rem !important;
  }
}

.pdf-container-height {
  height: 500px;
}

/* Фикс для fullscreen диалога - убираем огромный футер */
::v-deep .v-dialog--fullscreen {
  margin: 0 !important;
}

::v-deep .v-dialog--fullscreen .v-card {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  max-width: 100vw;
  margin: 0;
}

::v-deep .v-dialog--fullscreen .v-card-actions {
  flex: none;
  height: auto;
  min-height: auto;
}

/* Убираем все псевдоэлементы и margin/padding */
::v-deep .v-dialog--fullscreen .v-card-actions:before,
::v-deep .v-dialog--fullscreen .v-card-actions:after {
  display: none !important;
}

::v-deep .v-dialog--fullscreen .v-card-actions {
  margin: 0 !important;
}
</style>
